/**
 * The portrait anchor contract, in the game's own language.
 *
 * `tools/captain-art/anchors.mjs` is the authoring-side copy that the offline rejection
 * checks import. This file is the runtime copy, and the two are pinned to each other by
 * `tests/captain-portrait.test.ts` — if a number moves in one and not the other, the
 * gate fails. Explained in `reports/captain/ANCHORS.md`.
 *
 * Nothing here names a colour. The ramp is nineteen INDICES into `RAMP_TOKENS`, every one
 * of which is already a key in `src/render/palette.ts`, so a portrait cannot introduce a
 * hex the palette has not ratified and a colourblind preset remaps the face for free.
 */

/** Native canvas, square. Displayed at integer scale, nearest-neighbour, always. */
export const CANVAS = 64;

/** Mirror axis in pixel coordinates: between column 31 and column 32. */
export const MIRROR_X = 31.5;

/** Horizontal anchor rows, 0-indexed from the top. */
export const ROW = {
  hairCeil: 4,
  craniumTop: 8,
  browLine: 26,
  eyeLine: 30,
  noseBase: 40,
  mouthLine: 46,
  jawBottom: 55,
  shoulderLine: 58,
} as const;

/**
 * Inclusive column spans, symmetric about `MIRROR_X`, with the play each one allows.
 *
 * `tol` is the change from a fixed span to a tolerance, and it is the whole point of the
 * skull rework: pinning the cheek to an exact 15–48 made every captain identical at the
 * one row the eye reads first. Two pixels of play at the temple and cheek buys three
 * distinct head widths at the eye line without moving the eyes off row 30.
 */
export const WIDTH = {
  temple: { row: 20, x0: 16, x1: 47, tol: 3 },
  cheek: { row: 30, x0: 15, x1: 48, tol: 3 },
  jaw: { row: 48, x0: 19, x1: 44, tol: 3 },
  chin: { row: 55, x0: 23, x1: 40, tol: 2 },
} as const;

/** Pupil centres, 16 px apart, straddling the mirror axis. */
export const PUPIL_X: readonly [number, number] = [23.5, 39.5];
export const EYE_MAX_W = 11;

export type SlotId =
  | 'skull'
  | 'hair'
  | 'brow'
  | 'eyes'
  | 'nose'
  | 'mouth'
  | 'marks'
  | 'collar'
  | 'rank';

export interface Slot {
  /** `[x, y, w, h]` — a part has zero opaque pixels outside this. */
  box: readonly [number, number, number, number];
  mirrored: boolean;
  /** Row the slot's opaque row-centroid must land on, or null. */
  anchorRow: number | null;
  /** Permitted connected-component count, inclusive. */
  parts: readonly [number, number];
}

export const SLOTS: Readonly<Record<SlotId, Slot>> = {
  // 13…50, not 14…49: the ear stands one or two columns off the skull and that is one
  // of the few outline cues left once a hat has covered the hair.
  skull: { box: [13, 6, 38, 52], mirrored: true, anchorRow: ROW.eyeLine, parts: [1, 2] },
  // Wider and deeper than the skull on purpose. Hair that stays inside the skull cannot
  // change a silhouette, and silhouette is the only cue that survives at `HUD_SCALE`.
  hair: { box: [6, 2, 52, 46], mirrored: true, anchorRow: null, parts: [1, 4] },
  brow: { box: [15, 22, 34, 8], mirrored: true, anchorRow: ROW.browLine, parts: [1, 2] },
  eyes: { box: [15, 26, 34, 9], mirrored: true, anchorRow: ROW.eyeLine, parts: [2, 2] },
  nose: { box: [26, 33, 12, 9], mirrored: true, anchorRow: ROW.noseBase, parts: [1, 3] },
  mouth: { box: [21, 41, 22, 9], mirrored: true, anchorRow: ROW.mouthLine, parts: [1, 3] },
  marks: { box: [10, 18, 44, 36], mirrored: false, anchorRow: null, parts: [1, 3] },
  collar: { box: [4, 52, 56, 12], mirrored: true, anchorRow: null, parts: [1, 3] },
  // On the shoulder under the jaw, not out in the corner. See `drawRank`.
  rank: { box: [16, 56, 16, 6], mirrored: false, anchorRow: null, parts: [1, 2] },
};

/**
 * The nineteen-value ramp, index 0 transparent. Every entry is an existing palette token;
 * §11.14's "no raw hex outside the palette module" holds by construction here.
 *
 * Two arcs, and the split is the point. 1…10 are §11.2.6 — the sonar blues the panel
 * behind the portrait is already made of, plus black, white and the one reserved warm.
 * They dress the uniform, the ink, the grey hair and the field. 11…18 are the AMD-42
 * complexion ramp, and they exist because 1…10 contain no skin: seven of their eight
 * chromatic entries sit inside 200.2°…220.0°, so a face drawn from them is the same
 * colour as the grid behind it, which is what every captain used to be.
 *
 * The eight complexion values are APPENDED. Indices 1…10 keep their meaning, so nothing
 * that already reads a ramp index has to know this happened.
 */
export const RAMP_TOKENS: readonly string[] = [
  '', // 0 — transparent, never resolved
  'pal.ui.outline', // 1  #000000  ink
  'pal.ui.bg', // 2  #050A14  deep shadow
  'pal.ui.panel', // 3  #0A1B2E  shadow
  'pal.ui.grid', // 4  #16324A  cool mid-dark
  'pal.ui.gridmajor', // 5  #1E4A6B  mid
  'pal.ui.decor', // 6  #1E5B7A  mid-light
  'pal.ui.textdim', // 7  #8FB3CC  light
  'pal.ui.text', // 8  #E8F4FF  highlight
  'pal.ui.core', // 9  #FFFFFF  specular
  'pal.ui.accent', // 10 #FFD36B  iris, rank pin — the only warm value
  'pal.skin.0', // 11 #291B13  complexion shadow — never a base, see RC.minEdgeContrast
  'pal.skin.1', // 12 #432B1E
  'pal.skin.2', // 13 #5C3A29
  'pal.skin.3', // 14 #734B36
  'pal.skin.4', // 15 #8B5B44
  'pal.skin.5', // 16 #A26A52
  'pal.skin.6', // 17 #B37B65  the lightest value that does not cross the bright pass
  'pal.skin.7', // 18 #DDB9B0  complexion specular — blooms, spent like pal.ui.core
];

export const RAMP_SIZE = RAMP_TOKENS.length;

/** Named ramp indices, so a drawing routine reads as intent rather than as a number. */
export const V = {
  none: 0,
  ink: 1,
  deep: 2,
  shade: 3,
  cool: 4,
  mid: 5,
  midlight: 6,
  light: 7,
  high: 8,
  spec: 9,
  warm: 10,
  skin0: 11,
  skin1: 12,
  skin2: 13,
  skin3: 14,
  skin4: 15,
  skin5: 16,
  skin6: 17,
  skin7: 18,
} as const;

/**
 * Ramp indices whose max channel is over `TUBE.bloomThreshold` (0.72). Only these come
 * back out of the six-level pyramid as light rather than as pigment.
 *
 * This exists because the ramp is no longer ordered by luminance end to end — index 11
 * is darker than index 4 — so `value < V.high` stopped meaning "will not bloom" the
 * moment complexion was appended. Measured max channel: `pal.ui.textdim` 0.800,
 * `pal.ui.text` 1.000, `pal.ui.core` 1.000, `pal.ui.accent` 1.000, `pal.skin.7` 0.867.
 * Everything else is at or under 0.718.
 */
const BRIGHT = new Set<number>([V.light, V.high, V.spec, V.warm, V.skin7]);

/** True where a fill of `value` would bloom. A mass of it reads as a lamp, not a cheek. */
export function blooms(value: number): boolean {
  return BRIGHT.has(value);
}

/**
 * Ramp indices dark enough that an inked outline around them is invisible.
 *
 * `V.ink` is #000000 and the panel is `pal.ui.bg` #050A14 — the same colour to an eye —
 * so an outline only works when what it encloses is brighter than the panel. Displayed
 * luminance: `pal.ui.grid` 0.180 and `pal.skin.1` 0.185 are the top of this set; the next
 * values up are `pal.skin.2` 0.251 and `pal.ui.gridmajor` 0.263.
 */
const NEEDS_RIM = new Set<number>([V.ink, V.deep, V.shade, V.cool, V.skin0, V.skin1]);

/** True where a mass of `value` has to be rimmed with light instead of outlined in ink. */
export function needsRim(value: number): boolean {
  return NEEDS_RIM.has(value);
}

/** True for the AMD-42 complexion arc, which rims warm rather than cyan. */
export function isSkin(value: number): boolean {
  return value >= V.skin0 && value <= V.skin7;
}

/** Rejection thresholds, shared with the offline checker. */
export const RC = {
  inkMin: 0.06,
  inkMax: 0.65,
  centroidXTol: 1.0,
  mirrorResidualMax: 0.08,
  anchorRowTol: 1.0,
  minDistinctValues: 3,
  maxSingleValueShare: 0.8,
  /**
   * Displayed-luminance separation the head has to hold against the panel it ships on.
   * The first version of the ramp guarded the ink end and the specular end and missed
   * the background, which sits between them: a quarter of the roster dissolved.
   */
  minEdgeContrast: 0.1,
  /** A mark may not take more than half of either eye's sclera. */
  minEyeSclera: 0.5,
  /** Differing coverage pixels, rows 0…33, below which two captains share a head. */
  minSilhouetteHamming: 40,
} as const;
